import { useNavigate } from "react-router-dom";
import { Plus, Heart } from "lucide-react";
import { PassportStamp } from "@/components/motifs";
import { resolveImg, formatPrice, getImages, priceInfo, inStock } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { toggleWishlist, inWishlist } = useCustomer();
  const imgs = getImages(product);
  const cover = imgs[0];
  const hover = imgs[1];
  const { selling, strike, discount } = priceInfo(product);
  const available = inStock(product);
  const wished = inWishlist(product.id);

  const handleAdd = (e) => {
    e.stopPropagation();
    if (!available) return;
    addItem({ ...product, price: selling });
    toast.success(`${product.name} added to your flight bag`);
  };

  const handleWish = async (e) => {
    e.stopPropagation();
    const res = await toggleWishlist(product.id);
    if (res?.needAuth) { toast("Please log in to save favourites"); navigate("/account"); return; }
    toast.success(res.active ? "Added to wishlist" : "Removed from wishlist");
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/product/${product.id}`)}
      className="group cursor-pointer overflow-hidden rounded-xl border border-[#E7DFD3] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(120,90,20,0.35)]"
    >
      <div className="relative aspect-square overflow-hidden bg-[#F3EDE2]">
        <img src={resolveImg(cover)} alt={product.name} className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${!available ? "opacity-60" : ""}`} />
        {hover && (
          <img src={resolveImg(hover)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        )}
        {product.badge && (
          <div className="absolute left-3 top-3"><PassportStamp label={product.badge} /></div>
        )}

        <button
          data-testid={`wishlist-${product.id}`}
          onClick={handleWish}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/85 backdrop-blur transition-colors hover:bg-white"
          aria-label="Add to wishlist"
        >
          <Heart size={17} className={wished ? "fill-red-500 text-red-500" : "text-stone-500"} />
        </button>

        {discount > 0 && available && (
          <div className="absolute bottom-3 left-3 rounded-full bg-[#B91C1C] px-2 py-0.5 font-mono text-[10px] font-semibold text-white">-{discount}%</div>
        )}
        {!available && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-stone-900/80 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-white">Sold Out</span>
          </div>
        )}

        {available && (
          <button
            data-testid={`quick-add-${product.id}`}
            onClick={handleAdd}
            className="absolute bottom-3 right-3 flex h-11 w-11 translate-y-3 items-center justify-center rounded-full gold-gradient text-stone-900 opacity-0 shadow-lg transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
            aria-label="Add to bag"
          >
            <Plus size={20} />
          </button>
        )}
      </div>

      <div className="p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700/70">{product.category}</p>
        <h3 className="mt-1 font-serif text-lg leading-snug text-stone-900">{product.name}</h3>
        <p className="mt-0.5 text-xs text-stone-500">{product.material}</p>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-sans text-base font-semibold text-stone-900">{formatPrice(selling)}</span>
          {strike && <span className="font-sans text-sm text-stone-400 line-through">{formatPrice(strike)}</span>}
        </div>
      </div>
    </div>
  );
}
